import { Drawer } from '@/components';
import {
  useProject,
  useUpdateFundAdditionalAttributes,
} from '@/features/project/_hooks/project';
import { isBefore2025 } from '@/features/project/review-sheet/utils/project';
import { useId } from '@/hooks';
import { useMessage } from '@/providers';
import { boolToString } from '@/utils';
import dayjs from 'dayjs';
import { groupBy } from 'lodash-es';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FundAdditionalAttributesModel } from '../../../_models/project';
import { useProjectDetailDrawerState } from '../../_components/ProjectDrawerStateProvider';
import {
  FundAdditionalAttributesForm,
  emptyCompositionBackground,
} from './FundAdditionalAttributesForm';

export const FundAdditionalAttributeFormsDrawer = () => {
  const { t } = useTranslation();
  const { openMessage } = useMessage();
  const { drawerState, setDrawerState } = useProjectDetailDrawerState();

  const { id: projectId } = useId();
  const project = useProject(projectId);
  const { mutateAsync: updateFundAdditionalAttributes } =
    useUpdateFundAdditionalAttributes();

  const isKsLocalGovernmentAfter2025 = useMemo(
    () =>
      !isBefore2025(project.data) &&
      project.data?.fundAdditionalAttributes?.fundSheetType ===
        'local-government',
    [project.data],
  );

  if (project.isLoading || !project.data) {
    return null;
  }

  const parsedFundAdditionalAttributes =
    FundAdditionalAttributesModel.safeParse(
      project.data.fundAdditionalAttributes,
    );

  if (!parsedFundAdditionalAttributes.success) {
    return null;
  }

  const handleClose = async () => {
    setDrawerState({ open: false });
  };

  return (
    <Drawer
      size="large"
      open={
        drawerState.open && drawerState.type === 'fund-additional-attributes'
      }
      onClose={handleClose}
      destroyOnClose
    >
      <FundAdditionalAttributesForm
        fundSheetType={project.data?.fundAdditionalAttributes?.fundSheetType}
        fiscalYear={project.data.fiscalYear}
        initialValues={{
          ...parsedFundAdditionalAttributes.data,
          compositionBackgrounds:
            isKsLocalGovernmentAfter2025 &&
            (!parsedFundAdditionalAttributes.data.compositionBackgrounds ||
              parsedFundAdditionalAttributes.data.compositionBackgrounds
                .length === 0)
              ? [emptyCompositionBackground]
              : parsedFundAdditionalAttributes.data.compositionBackgrounds?.map(
                  ({ expenditureBudgetPurposes, ...background }) => {
                    return {
                      ...background,
                      expenditureBudgetPurposeIds: Object.entries(
                        groupBy(
                          expenditureBudgetPurposes?.map(
                            (expenditureBudgetPurposes) => ({
                              ...expenditureBudgetPurposes,
                              upperExpenditurePurposeId:
                                expenditureBudgetPurposes.expenditurePurpose
                                  .upperId,
                              upperExpenditurePurpose:
                                expenditureBudgetPurposes.expenditurePurpose
                                  .upper,
                            }),
                          ),
                          'jurisdictionId',
                        ),
                      ).flatMap(([jurisdictionId, expenditureBudgetPurposes]) =>
                        Object.entries(
                          groupBy(
                            expenditureBudgetPurposes,
                            (expenditureBudgetPurpose) =>
                              expenditureBudgetPurpose.accountingType.type,
                          ),
                        ).flatMap(([type, expenditureBudgetPurposes]) => {
                          if (type === 'general') {
                            return Object.entries(
                              groupBy(
                                expenditureBudgetPurposes,
                                'organizationId',
                              ),
                            ).flatMap(
                              ([organizationId, expenditureBudgetPurposes]) =>
                                Object.entries(
                                  groupBy(
                                    expenditureBudgetPurposes,
                                    'upperExpenditurePurposeId',
                                  ),
                                ).flatMap(
                                  ([
                                    upperExpenditurePurposeId,
                                    expenditureBudgetPurposes,
                                  ]) =>
                                    expenditureBudgetPurposes.map(
                                      (expenditureBudgetPurpose) => {
                                        return [
                                          expenditureBudgetPurpose.accountingId,
                                          jurisdictionId,
                                          organizationId,
                                          upperExpenditurePurposeId,
                                          expenditureBudgetPurpose.id,
                                        ];
                                      },
                                    ),
                                ),
                            );
                          }

                          return Object.entries(
                            groupBy(expenditureBudgetPurposes, 'accountingId'),
                          ).flatMap(
                            ([accountingId, expenditureBudgetPurposes]) =>
                              Object.entries(
                                groupBy(
                                  expenditureBudgetPurposes,
                                  'upperExpenditurePurposeId',
                                ),
                              ).flatMap(
                                ([
                                  upperExpenditurePurposeId,
                                  expenditureBudgetPurposes,
                                ]) =>
                                  expenditureBudgetPurposes.map(
                                    (expenditureBudgetPurpose) => [
                                      accountingId,
                                      jurisdictionId,
                                      upperExpenditurePurposeId,
                                      expenditureBudgetPurpose.id,
                                    ],
                                  ),
                              ),
                          );
                        }),
                      ),
                      haveAppliedLawOfAppropriatenessOfSubsidies: boolToString(
                        background.haveAppliedLawOfAppropriatenessOfSubsidies,
                      ),
                      fiscalYearOfBudgetaryProvision:
                        background.fiscalYearOfBudgetaryProvision
                          ? dayjs().year(
                              background.fiscalYearOfBudgetaryProvision,
                            )
                          : undefined,
                      fiscalYearOfCreation: background.fiscalYearOfCreation
                        ? dayjs().year(background.fiscalYearOfCreation)
                        : undefined,
                      relatedReviewSheetYear: background.relatedReviewSheetYear
                        ? dayjs().year(background.relatedReviewSheetYear)
                        : undefined,
                    };
                  },
                ),
          nationalTreasuryReturnBackgrounds:
            parsedFundAdditionalAttributes.data.nationalTreasuryReturnBackgrounds?.map(
              (natioanlTreasury) => {
                return {
                  ...natioanlTreasury,
                  fiscalYear: natioanlTreasury.fiscalYear
                    ? dayjs().year(natioanlTreasury.fiscalYear)
                    : undefined,
                };
              },
            ),
          scheduledEnd: {
            ...parsedFundAdditionalAttributes.data.scheduledEnd,
            endDate: parsedFundAdditionalAttributes.data.scheduledEnd?.endDate
              ? dayjs(parsedFundAdditionalAttributes.data.scheduledEnd.endDate)
              : undefined,
            endDateOfNewApplicationReceiving: parsedFundAdditionalAttributes
              .data.scheduledEnd?.endDateOfNewApplicationReceiving
              ? dayjs(
                  parsedFundAdditionalAttributes.data.scheduledEnd
                    .endDateOfNewApplicationReceiving,
                )
              : undefined,
          },
        }}
        onSubmit={async (values) => {
          await updateFundAdditionalAttributes({
            id: projectId,
            ...values,
          });
          await Promise.all([
            openMessage('success', t('message-update-completed')),
            handleClose(),
          ]);
        }}
      />
    </Drawer>
  );
};
